import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  InputAdornment,
  Fade,
  CircularProgress,
  Pagination,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Search,
  Clear,
  PlayArrow,
  BookmarkBorder,
  ArrowBack,
  Favorite,
  FavoriteBorder,
  ShoppingCart,
  MenuBook,
  AttachMoney
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchEbooks, filterEbooks, fetchAllMetadata } from '../api/ebooks';

import { useTheme } from '@mui/material/styles';
import NavBar from '../components/NavBar';
import Modal from '@mui/material/Modal';
import EBookModal from '../components/EBookModal';
import { useAuth } from '../contexts/AuthContext';

const getInitialFavourites = () => {
  try {
    return JSON.parse(localStorage.getItem('favouriteEbooks')) || [];
  } catch {
    return [];
  }
};

// Helper function to parse comma-separated values or return array as-is
const parseCommaSeparated = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }
  return [];
};

const EBookCatalog = () => {
  const [ebooks, setEbooks] = useState([]);
  const [filteredEbooks, setFilteredEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ chakras: [], emotions: [], categories: [] });
  const [favourites, setFavourites] = useState(getInitialFavourites());
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEbook, setModalEbook] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(16);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  useEffect(() => {
    // Load all ebooks on component mount
    const loadEbooks = async () => {
      try {
        const [metadata, allEbooks] = await Promise.all([
          fetchAllMetadata(),
          fetchEbooks()
        ]);
        setEbooks(allEbooks);
        setFilteredEbooks(allEbooks);
        
        console.log('Raw metadata:', metadata);
        console.log('metadata.ebookCategories:', metadata.ebookCategories);
        
        const fo = {
            categories: Array.from(metadata.ebookCategories.map(c => c.category) || []).sort()
          }
        console.log('Processed filterOptions:', fo)
        setFilterOptions(fo);
      } catch (error) {
        console.error('Error loading ebooks:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEbooks();
  }, []);

  useEffect(() => {
    // Apply filters when search term or filters change
    const applyFilters = async () => {
      setLoading(true);
      try {
        if (!searchTerm && !categoryFilter) {
          setFilteredEbooks(ebooks);
        } else {
          const filtered = await filterEbooks(ebooks, searchTerm, categoryFilter);
          setFilteredEbooks(filtered);
        }
      } catch (error) {
        console.error('Error filtering ebooks:', error);
        // On error, show unfiltered ebooks
        setFilteredEbooks(ebooks);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimeout = setTimeout(applyFilters, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, categoryFilter, ebooks]);

  useEffect(() => {
    localStorage.setItem('favouriteEbooks', JSON.stringify(favourites));
  }, [favourites]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClearCategoryFilter = () => {
    setCategoryFilter('');
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (categoryFilter) count++;
    return count;
  };

  const toggleFavourite = (id) => {
    setFavourites((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  const openPurchaseModal = (ebook) => {
    setModalEbook(ebook);
    setModalOpen(true);
  };
  
  const closePurchaseModal = () => {
    setModalOpen(false);
    setModalEbook(null);
  };

  const filtered = showFavouritesOnly
    ? filteredEbooks.filter((e) => favourites.includes(e.id))
    : filteredEbooks;

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageEbooks = filtered.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, showFavouritesOnly]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const EBookCard = ({ ebook }) => {
    const isDark = theme.palette.mode === 'dark';
    const tags = parseCommaSeparated(ebook.tags);
    
    return (
      <Fade in={true} timeout={500}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '&:hover': {
              transform: 'translateY(-4px)',
            }
          }}
        >
          {/* Background Image */}
          {ebook.imageUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${ebook.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: isDark ? 0.15 : 0.1,
                zIndex: 0
              }}
            />
          )}
          
          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, ${theme.palette.background.paper}CC 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.background.paper}CC 100%)`,
              zIndex: 1
            }}
          />

          <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                {ebook.title}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={() => toggleFavourite(ebook.id)} size="small" color="error">
                  {favourites.includes(ebook.id) ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary" >{ebook.authorName}</Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minHeight: '4.8em',
                mt: 1
              }}
            >
              {ebook.excerpt}
            </Typography>

            <Box flexGrow={1} />
            
            {/* Price Display */}
            <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
              <Typography variant="h5" fontWeight={700} color="primary">
                {ebook.priceDisplay}
              </Typography>
              {ebook.freebie === 1 && (
                <Chip 
                  label="FREE" 
                  size="small" 
                  color="success" 
                  onClick={(e) => e.stopPropagation()}
                  sx={{ ml: 1, fontWeight: 600, cursor: 'default' }}
                />
              )}
            </Box>
            
            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
              {/* Category Chip */}
              {ebook.category && (
                <Chip
                  label={ebook.category}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'default'
                  }}
                />
              )}

              {/* Tags */}
              {tags && tags.length > 0 && (
                <Chip
                  label={tags[0]}
                  size="small"
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    borderWidth: 2,
                    cursor: 'default'
                  }}
                />
              )}
            </Box>
          </CardContent>

          <CardActions sx={{ p: 3, pt: 0, flexShrink: 0, position: 'relative', zIndex: 2 }}>
            <Button
              variant="contained"
              startIcon={<ShoppingCart />}
              size="small"
              sx={{ mr: 1, flex: 1 }}
              onClick={() => openPurchaseModal(ebook)}
            >
              {ebook.freebie === 1 ? 'Download' : 'Buy Now'}
            </Button>
          </CardActions>
        </Card>
      </Fade>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        py: 0
      }}
    >
      <NavBar user={user} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box mb={4} display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Discover Your Perfect EBook
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Explore our curated collection of spiritual and wellness ebooks
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant={showFavouritesOnly ? 'contained' : 'outlined'}
              color="error"
              startIcon={<Favorite />}
              onClick={() => setShowFavouritesOnly((v) => !v)}
              sx={{ minWidth: 180 }}
            >
              {showFavouritesOnly ? 'Showing Favourites' : 'Show Favourites Only'}
            </Button>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Box mb={4}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: '2fr 1fr 1fr 1fr 1fr',
                lg: '2fr 1fr 1fr 1fr 1fr'
              },
              gap: 2,
              alignItems: 'center',
              mb: 2
            }}
          >
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search ebooks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Category Filter */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {filterOptions.categories?.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>

            {/* Clear Filters */}
            {getActiveFiltersCount() > 0 && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClearAllFilters}
                startIcon={<Clear />}
                sx={{ minHeight: 56 }}
              >
                Clear ({getActiveFiltersCount()})
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {getActiveFiltersCount() > 0 && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  onDelete={handleClearSearch}
                  onClick={handleClearSearch}
                  color="primary"
                />
              )}
              {categoryFilter && (
                <Chip
                  label={`Category: ${categoryFilter}`}
                  onDelete={handleClearCategoryFilter}
                  onClick={handleClearCategoryFilter}
                  color="info"
                />
              )}
            </Box>
          )}
        </Box>

        {/* Results */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Typography variant="h6" color="text.secondary">
            {loading ? 'Loading...' : `${filtered.length} ebook${filtered.length !== 1 ? 's' : ''} found`}
          </Typography>
          {!loading && filtered.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </Typography>
          )}
        </Box>

        {/* EBooks Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No ebooks found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Box>
        ) : currentPageEbooks.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No ebooks on this page
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try going to an earlier page or changing your page size
            </Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              sx={{ mt: 2 }}
              onClick={() => setCurrentPage(1)}
            >
              Go to First Page
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)'
              },
              gap: 3,
              alignItems: 'stretch'
            }}
          >
            {currentPageEbooks.map((ebook) => (
              <EBookCard key={ebook.id} ebook={ebook} />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box mt={4} display="flex" justifyContent="center">
            <Stack spacing={3} alignItems="center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'medium' : 'large'}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={isMobile ? 1 : 2}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    color: theme.palette.text.primary,
                    '&.Mui-selected': {
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    },
                  },
                }}
              />
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                  Items per page:
                </Typography>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      '& .MuiSelect-select': {
                        paddingY: 1,
                        paddingX: 2,
                      },
                    }}
                  >
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={16}>16</MenuItem>
                    <MenuItem value={32}>32</MenuItem>
                    <MenuItem value={64}>64</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Box>
        )}
      </Container>
      
      {/* Purchase Modal */}
      <EBookModal
        open={modalOpen}
        onClose={closePurchaseModal}
        title={modalEbook?.title || ''}
        author={modalEbook?.authorName || ''}
        excerpt={modalEbook?.excerpt || ''}
        price={modalEbook?.price || 0}
        priceDisplay={modalEbook?.priceDisplay || ''}
        coverImageUrl={modalEbook?.imageUrl || ''}
      />
    </Box>
  );
};

export default EBookCatalog; 